/**
 * Event-driven Authentication - No more token refresh storms
 * Uses Firebase SDK events instead of polling for tokens
 */

import { getAuth, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';

const auth = getAuth();

let cachedToken = null;
let cachedExp = 0; // epoch seconds
let inflight = null;

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

let state = {
  user: null,
  claims: null,
  idToken: null,
  inFlight: null, // prevents concurrent fetches
  lastClaimsAt: 0,
};

let listeners = new Set();

// Call this once at app bootstrap to keep token fresh in the background
export function startAuthTokenAutoRefresh(auth) {
  onIdTokenChanged(auth, async (user) => {
    if (!user) {
      cachedToken = null;
      cachedExp = 0;
      return;
    }
    // Refresh once per ID token change, no hammering
    try {
      const res = await user.getIdTokenResult(/* forceRefresh */ false);
      cachedToken = res.token;
      cachedExp = res.claims?.exp ?? (nowSec() + 55 * 60);
    } catch (e) {
      // Don't loop; keep last good token (if any)
      console.warn("[Auth] onIdTokenChanged token fetch error:", e?.code || e);
    }
  });
}

// Use this everywhere instead of calling getIdToken() directly
export async function getCachedIdToken(user, { minTTL = 120 } = {}) {
  if (!user) return null;

  const needsRefresh = !cachedToken || (cachedExp - nowSec()) < minTTL;
  if (!needsRefresh) return cachedToken;

  // De-dupe concurrent refreshes
  if (!inflight) {
    inflight = (async () => {
      try {
        const res = await user.getIdTokenResult(false); // NEVER force in loops
        cachedToken = res.token;
        cachedExp = res.claims?.exp ?? (nowSec() + 55 * 60);
        return cachedToken;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

// Notify all listeners of auth state changes
function notifyListeners() {
  const snapshot = { 
    ready: state.user !== undefined, 
    user: state.user, 
    claims: state.claims || {} 
  };
  listeners.forEach(callback => callback(snapshot));
}

/**
 * Subscribe to auth state changes (single subscription point for the whole app)
 * @param {Function} onChange - Callback function that receives { ready, user, claims }
 * @returns {Function} Unsubscribe function
 */
export function subscribeAuth(onChange) {
  listeners.add(onChange);
  
  // If we already have state, notify immediately
  if (state.user !== undefined) {
    onChange({ ready: true, user: state.user, claims: state.claims || {} });
  }

  // Single subscription point for auth state changes
  const unsubAuth = onAuthStateChanged(auth, (user) => {
    console.log('🔄 AUTH: Auth state changed:', { hasUser: !!user, uid: user?.uid });
    state.user = user || null;
    notifyListeners();
  });

  // Update claims only when Firebase rotates tokens (rare) or sign-in changes
  const unsubToken = onIdTokenChanged(auth, async (user) => {
    if (!user) {
      console.log('🔄 AUTH: User signed out, clearing claims');
      state.claims = null;
      state.idToken = null;
      notifyListeners();
      return;
    }

    // Debounce: don't fetch claims more than once per minute
    const now = Date.now();
    if (now - state.lastClaimsAt < 60_000) {
      console.log('🔄 AUTH: Claims fetch debounced (too recent)');
      return;
    }
    
    if (state.inFlight) {
      console.log('🔄 AUTH: Claims fetch already in progress, waiting...');
      await state.inFlight;
      return;
    }

    console.log('🔄 AUTH: Fetching fresh claims (token changed)');
    state.inFlight = (async () => {
      try {
        // DO NOT force refresh; let SDK decide when to refresh
        const res = await user.getIdTokenResult(false);
        state.idToken = res.token;
        state.claims = res.claims || {};
        state.lastClaimsAt = Date.now();
        console.log('✅ AUTH: Claims updated:', { admin: state.claims?.admin, host: state.claims?.host });
      } catch (error) {
        console.error('❌ AUTH: Error fetching claims:', error);
        // Don't clear existing claims on error, just log it
      } finally {
        state.inFlight = null;
      }
      notifyListeners();
    })();
    
    await state.inFlight;
  });

  return () => { 
    listeners.delete(onChange);
    unsubAuth(); 
    unsubToken(); 
  };
}

/**
 * Get current auth snapshot (synchronous)
 * @returns {Object} { ready, user, claims }
 */
export function getAuthSnapshot() {
  return { 
    ready: state.user !== undefined, 
    user: state.user, 
    claims: state.claims || {} 
  };
}

/**
 * Get cached claims (synchronous, no network calls)
 * @returns {Object} User claims or empty object
 */
export function getCachedClaims() {
  return state.claims || {};
}


/**
 * Check if user has admin access (synchronous, uses cached claims)
 * @returns {boolean} True if user has admin access
 */
export function isAdmin() {
  return state.claims?.admin === true;
}

/**
 * Check if user has creator access (synchronous, uses cached claims)
 * @returns {boolean} True if user has active creator subscription
 */
export function hasCreatorAccess() {
  return state.claims?.creator === true && state.claims?.creatorActive === true;
}

/**
 * Force refresh claims (use sparingly, only when absolutely necessary)
 * @returns {Promise<Object>} Fresh claims
 */
export async function forceRefreshClaims() {
  if (!state.user) {
    throw new Error('No authenticated user');
  }

  // Wait for any in-flight request to complete
  if (state.inFlight) {
    await state.inFlight;
  }

  try {
    console.log('🔄 AUTH: Force refreshing claims (admin action)');
    const res = await state.user.getIdTokenResult(false); // Let SDK decide when to refresh
    state.idToken = res.token;
    state.claims = res.claims || {};
    state.lastClaimsAt = Date.now();
    notifyListeners();
    return state.claims;
  } catch (error) {
    console.error('❌ AUTH: Error force refreshing claims:', error);
    throw error;
  }
}

/**
 * Clear auth cache (for logout)
 */
export function clearAuthCache() {
  state.user = null;
  state.claims = null;
  state.idToken = null;
  state.inFlight = null;
  state.lastClaimsAt = 0;
  listeners.clear();
  console.log('🗑️ AUTH: Cache cleared');
}

/**
 * Get auth cache statistics
 * @returns {Object} Cache statistics
 */
export function getAuthCacheStats() {
  return {
    hasUser: !!state.user,
    hasClaims: !!state.claims,
    hasToken: !!state.idToken,
    inFlight: !!state.inFlight,
    lastClaimsAt: state.lastClaimsAt,
    listenerCount: listeners.size
  };
}

// Legacy exports for backward compatibility (deprecated)
export const getOptimizedIdTokenResult = () => {
  console.warn('⚠️ AUTH: getOptimizedIdTokenResult is deprecated, use getCachedIdToken() instead');
  return Promise.resolve({
    token: state.idToken,
    claims: state.claims,
    authTime: state.user?.metadata?.lastSignInTime,
    issuedAtTime: state.user?.metadata?.creationTime,
    expirationTime: Date.now() + 3600000 // 1 hour from now
  });
};

export const getUserClaimsWithCache = () => {
  console.warn('⚠️ AUTH: getUserClaimsWithCache is deprecated, use getCachedClaims() instead');
  return Promise.resolve(state.claims || {});
};

export const hasCreatorAccessWithCache = () => {
  console.warn('⚠️ AUTH: hasCreatorAccessWithCache is deprecated, use hasCreatorAccess() instead');
  return Promise.resolve(hasCreatorAccess());
};

export const forceRefreshToken = () => {
  console.warn('⚠️ AUTH: forceRefreshToken is deprecated, use forceRefreshClaims() instead');
  return forceRefreshClaims();
};